<script lang="ts" setup>
import { useUserProfile } from '@auth/useProfile';
import { useErrorHandler } from '@errors/useErrorHandler';
import { faCog as farCog, faRightFromBracket, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faCog as fasCog } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { CommonDomainMarktsegment } from '@services/identity-api/orval-api/model/commonDomainMarktsegment';
import { postApiV1UserWijzigMarktsegment } from '@services/identity-api/orval-api/user-api/user-api';
import type { MenuItemCommandEvent } from 'primevue/menuitem';
import VAvatar from '@shared/component/VAvatar/VAvatar.vue';
import VMenu from '@shared/component/VMenu/VMenu.vue';
import { navigateToUrlWithPostRequest } from '@tools/helpers';
import { computed, ref } from 'vue';
import { useClaims } from '@auth/useClaims';
import { instellingenPaginaLink, tryMenuNavigation } from '../../urlHelper';
import AppLayoutMenuMarktsegmenten from './AppLayoutMenuMarktsegmenten.vue';

const props = withDefaults(
  defineProps<{
    alwaysSmall?: boolean;
    iconStyle?: 'far' | 'fas';
  }>(),
  { iconStyle: 'far' },
);

const menu = ref<InstanceType<typeof VMenu>>();
const claims = useClaims();

const { userProfile, userProfileIsPending } = useUserProfile();
const { handleError } = useErrorHandler();

function toggleMenu(event: Event) {
  menu.value?.toggle(event);
}

const instellingenLink = computed(() =>
  instellingenPaginaLink(claims.value?.heeftInstellingenPermissie ?? false),
);

const marktsegmenten = computed(() => userProfile.value?.beschikbareMarktsegmenten ?? []);

const accountIcon = computed(() => (props.iconStyle === 'fas' ? fasCog : farCog));

async function wijzigMarktsegment(marktsegment: CommonDomainMarktsegment) {
  try {
    await postApiV1UserWijzigMarktsegment({ marktsegment });
    window.location.reload(); // nodig zodat claims/context direct zijn bijgewerkt
  } catch (error) {
    handleError(error as Error, {
      onError: 'Het wijzigen van het marktsegment is mislukt. Probeer het opnieuw.',
    });
  }
}
</script>

<template>
  <div class="relative flex">
    <button
      title="Account"
      class="flex cursor-pointer items-center gap-1 border-none bg-transparent px-4 text-sm text-inherit outline-none"
      :class="{
        'lg:px-6': !props.alwaysSmall,
      }"
      type="button"
      @click="toggleMenu"
    >
      <FontAwesomeIcon size="lg" :icon="accountIcon" />
    </button>

    <VMenu
      append-to="self"
      class="top-full! right-0! left-auto! min-w-64!"
      :model="[
        {
          command: ({ originalEvent }: MenuItemCommandEvent) =>
            tryMenuNavigation(originalEvent, '/identity/user/account?returnUrl=%2Fcontent%2F'),
          icon: faUser,
          label: 'Account',
          url: '/identity/user/account?returnUrl=%2Fcontent%2F',
        },
        {
          icon: farCog,
          label: 'Instellingen',
          url: instellingenLink,
          target: '_top',
        },
        {
          command: ({ originalEvent }: MenuItemCommandEvent) => {
            originalEvent.preventDefault();
            navigateToUrlWithPostRequest('/Identity/Authentication/Logout');
          },
          icon: faRightFromBracket,
          label: 'Uitloggen',
        },
      ]"
      popup
      ref="menu"
    >
      <template #start>
        <div
          v-if="!userProfileIsPending"
          class="grid grid-cols-[auto_1fr] grid-rows-2 gap-x-3 border-0 border-b border-solid border-grey-200 p-4"
        >
          <VAvatar class="row-span-2 self-center" :name="userProfile!.fullName" />

          <span class="flex items-end text-sm text-primary-900">
            {{ userProfile!.fullName }}
          </span>

          <span class="text-xs text-grey-400">
            {{ userProfile!.tussenPersoon }}
          </span>
        </div>

        <AppLayoutMenuMarktsegmenten
          v-if="marktsegmenten.length > 1"
          :huidig-marktsegment="claims?.marktsegment"
          :marktsegmenten="marktsegmenten"
          @select="wijzigMarktsegment"
        />
      </template>
    </VMenu>
  </div>
</template>
